import { useAuth } from '@clerk/clerk-react'
import { useCallback, useMemo } from 'react'

/**
 * Clerk-authenticated API client for V2 routes.
 *
 * Usage:
 *   const api = useV2Api()
 *   const sub = await api.getSubscription()
 *
 * Tokens are fetched fresh on every call via `getToken()` so we always
 * send a current, short-lived session token — no cache, no race with
 * Clerk's internal refresh.
 */
const API_BASE = import.meta.env.VITE_API_URL || ''

export function useV2Api() {
  const { getToken, isSignedIn } = useAuth()

  const request = useCallback(async (path, options = {}) => {
    if (!isSignedIn) {
      throw new Error('Not signed in')
    }

    const token = await getToken()
    const { signal, ...rest } = options
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(rest.headers || {})
    }

    const response = await fetch(`${API_BASE}${path}`, { ...rest, headers, signal })

    if (!response.ok) {
      let errorBody
      try {
        errorBody = await response.json()
      } catch {
        errorBody = { error: await response.text() || response.statusText }
      }
      const err = new Error(errorBody?.error || errorBody?.message || `HTTP ${response.status}`)
      err.status = response.status
      err.body = errorBody
      throw err
    }

    // Responses with no content (204, DELETE) — return null rather than throwing on JSON parse
    const text = await response.text()
    return text ? JSON.parse(text) : null
  }, [getToken, isSignedIn])

  const requestBlob = useCallback(async (path, options = {}) => {
    if (!isSignedIn) {
      throw new Error('Not signed in')
    }

    const token = await getToken()
    const { signal, ...rest } = options
    const headers = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(rest.headers || {})
    }

    const response = await fetch(`${API_BASE}${path}`, { ...rest, headers, signal })

    if (!response.ok) {
      let errorBody
      try {
        errorBody = await response.json()
      } catch {
        errorBody = { error: await response.text() || response.statusText }
      }
      const err = new Error(errorBody?.error || errorBody?.message || `HTTP ${response.status}`)
      err.status = response.status
      err.body = errorBody
      throw err
    }

    return response.blob()
  }, [getToken, isSignedIn])

  return useMemo(() => ({
    request,

    // "Me" endpoints — real tenant / subscription / team data
    getMe:            () => request('/api/v2/me'),
    getTenant:        () => request('/api/v2/me/tenant'),
    updateTenant:     (body) => request('/api/v2/tenants/me', {
                        method: 'PUT',
                        body: JSON.stringify(body)
                      }),
    updateTenantSettings: (settings) => request('/api/v2/tenants/me/settings', {
                            method: 'PUT',
                            body: JSON.stringify({ settings })
                          }),
    getSubscription:  () => request('/api/v2/me/subscription'),
    getTeam:          () => request('/api/v2/me/team'),

    // Email / SMTP settings (Stage 3 — endpoints land as they're implemented)
    getEmailSettings: () => request('/api/v2/me/email'),
    putSmtp:          (body) => request('/api/v2/me/email/smtp', {
                        method: 'PUT',
                        body: JSON.stringify(body)
                      }),
    testSmtp:         (body) => request('/api/v2/me/email/smtp/test', {
                        method: 'POST',
                        body: JSON.stringify(body)
                      }),
    connectNylas:     () => request('/api/v2/me/email/nylas/connect', { method: 'POST' }),
    revokeNylas:      () => request('/api/v2/me/email/nylas', { method: 'DELETE' }),

    // Chat / quote-assistant sessions
    listChatSessions:  () => request('/api/v2/chat/sessions'),
    createChatSession: () => request('/api/v2/chat/sessions', { method: 'POST' }),
    getChatSession:    (id) => request(`/api/v2/chat/sessions/${id}`),
    postChatMessage:   (id, content, opts = {}) => request(`/api/v2/chat/sessions/${id}/messages`, {
                         method: 'POST',
                         body: JSON.stringify({ content }),
                         signal: opts.signal
                       }),
    submitChatSession: (id, opts = {}) => request(`/api/v2/chat/sessions/${id}/submit`, {
                         method: 'POST',
                         signal: opts.signal
                       }),
    /**
     * Pre-seed a chat session's structured `state` from a form payload (e.g.
     * Quick Fill card). Backend filters keys + normalises incoterm before
     * persisting. Empty / non-whitelisted values are dropped silently.
     */
    patchChatSessionState: (id, patch, opts = {}) =>
                             request(`/api/v2/chat/sessions/${id}/state`, {
                               method: 'PATCH',
                               body: JSON.stringify({ patch }),
                               signal: opts.signal
                             }),

    // Port catalogue + form intake
    searchPorts: (q, type, opts = {}) => request(`/api/v2/ports?${new URLSearchParams({
                    q,
                    ...(type ? { type } : {}),
                    ...(opts.suggestNearest ? { suggestNearest: 'true' } : {})
                  })}`),
    submitNewQuote: (body) => request('/api/v2/quotes/new', {
                      method: 'POST',
                      body: JSON.stringify(body)
                    }),
    placeDetails: (placeId) => request(`/api/v2/places/geocode?${new URLSearchParams({ placeId })}`),

    // ────────────────────────────────────────────────────────────────────────
    // Workspace — shipment-as-thread surface (V2WorkspacePage)
    // ────────────────────────────────────────────────────────────────────────
    listShipments:        (filters = {}) => {
                            const params = new URLSearchParams()
                            Object.entries(filters).forEach(([k, v]) => {
                              if (v !== undefined && v !== null && v !== '') {
                                params.append(k, String(v))
                              }
                            })
                            const qs = params.toString()
                            return request(`/api/v2/shipments${qs ? `?${qs}` : ''}`)
                          },
    deleteShipment:       (id) => request(`/api/v2/shipments/${id}`, { method: 'DELETE' }),
    deleteChatSession:    (id) => request(`/api/v2/chat/sessions/${id}`, { method: 'DELETE' }),
    getShipment:          (id, options) => request(`/api/v2/shipments/${id}`, options),
    getShipmentActivity:  (id, options) => request(`/api/v2/shipments/${id}/activity`, options),
    /** Per-field shipment patch. Pass `notify: true` to post a system event. */
    patchShipment:        (id, fields) => request(`/api/v2/shipments/${id}`, {
                            method: 'PATCH',
                            body: JSON.stringify(fields)
                          }),
    postShipmentMessage:  (id, content, role = 'user', extra = {}) =>
                            request(`/api/v2/shipments/${id}/messages`, {
                              method: 'POST',
                              body: JSON.stringify({ content, role, ...extra })
                            }),
    shareIntent:          (id, quoteIds) =>
                            request(`/api/v2/shipments/${id}/share-intent`, {
                              method: 'POST',
                              body: JSON.stringify({ quoteIds })
                            }),
    dismissActivityItem:  (shipmentId, itemId) =>
                            request(`/api/v2/shipments/${shipmentId}/activity/${itemId}/dismiss`, {
                              method: 'POST'
                            }),
    suggestProviders:     (id) => request(`/api/v2/shipments/${id}/suggested-providers`),
    requestQuotes:        (id, providerIds) => request(`/api/v2/shipments/${id}/request-quotes`, {
                            method: 'POST',
                            body: JSON.stringify({ providerIds })
                          }),
    createComparison:     (id) => request(`/api/v2/shipments/${id}/compare`, { method: 'POST' }),
    selectQuote:          (shipmentId, quoteId, body) => request(`/api/v2/shipments/${shipmentId}/quotes/${quoteId}/select`, {
                            method: 'POST',
                            body: JSON.stringify(body)
                          }),
    confirmBookingIntent: (shipmentId, body) => request(`/api/v2/shipments/${shipmentId}/booking-intent`, {
                            method: 'POST',
                            body: JSON.stringify(body)
                          }),
    getQuotePdfBlob:      (shipmentId, quoteId) => requestBlob(`/api/v2/shipments/${shipmentId}/quotes/${quoteId}/pdf`),

    // ────────────────────────────────────────────────────────────────────────
    // Providers — workspace manual-add for the picker panel
    // ────────────────────────────────────────────────────────────────────────
    createProvider:       (body) => request('/api/v2/providers', {
                            method: 'POST',
                            body: JSON.stringify(body)
                          }),

    // ────────────────────────────────────────────────────────────────────────
    // Analytics — quote explorer with composite filters
    // ────────────────────────────────────────────────────────────────────────
    queryQuoteAnalytics:  (filters = {}) => {
                            const params = new URLSearchParams()
                            Object.entries(filters).forEach(([k, v]) => {
                              if (v !== undefined && v !== null && v !== '') params.append(k, v)
                            })
                            return request(`/api/v2/analytics/quotes?${params}`)
                          },
    getAnalyticsOptions:  () => request('/api/v2/analytics/quotes/options'),
    exportQuoteAnalytics: (filters = {}) => {
                            const params = new URLSearchParams()
                            Object.entries(filters).forEach(([k, v]) => {
                              if (v !== undefined && v !== null && v !== '') params.append(k, v)
                            })
                            return request(`/api/v2/analytics/quotes/export?${params}`)
                          }
  }), [request, requestBlob])
}
